import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { theme } from '../theme';
import { supabase } from '../supabaseClient';

// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function Payment() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('annual');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = {
    monthly: {
      name: 'Monthly',
      price: '$7',
      period: '/month',
      total: '$7 billed monthly',
      savings: null,
      priceId: 'price_1T34rw4BtZf1dc3czxMwYTvy',
    },
    annual: {
      name: 'Annual',
      price: '$49',
      period: '/year',
      total: '$49 billed annually',
      savings: 'Save $35 per year',
      priceId: 'price_1T34rq4BtZf1dc3cnoDFlxlA',
    },
  };

  const handlePayment = async () => {
  try {
    setLoading(true);
    setError('');

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      setError('Please log in to subscribe');
      navigate('/login');
      return;
    }

    // Call backend API to create checkout session
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId: plans[selectedPlan].priceId,
        userId: user.id,
        userEmail: user.email,
      }),
    });

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    // Get the checkout URL from the session
    const checkoutUrl = data.url;
    
    if (!checkoutUrl) {
      throw new Error('No checkout URL returned');
    }

    // Redirect to Stripe Checkout using the URL
    window.location.href = checkoutUrl;

  } catch (err) {
    console.error('Payment error:', err);
    setError(err.message || 'Something went wrong. Please try again.');
    setLoading(false);
  }
};

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Choose Your Plan</h1>
        <p style={styles.subtitle}>
          Start transforming your life with daily affirmation scrolls
        </p>

        {error && (
          <div style={styles.errorBox}>
            {error}
          </div>
        )}

        {/* Plan Selection */}
        <div style={styles.planContainer}>
          {Object.entries(plans).map(([key, plan]) => (
            <div
              key={key}
              style={{
                ...styles.planCard,
                ...(selectedPlan === key ? styles.planCardSelected : {}),
              }}
              onClick={() => setSelectedPlan(key)}
            >
              {plan.savings && (
                <div style={styles.savingsBadge}>{plan.savings}</div>
              )}
              <h3 style={styles.planName}>{plan.name}</h3>
              <div style={styles.priceContainer}>
                <span style={styles.price}>{plan.price}</span>
                <span style={styles.period}>{plan.period}</span>
              </div>
              <p style={styles.totalPrice}>{plan.total}</p>
              <div style={styles.radioContainer}>
                <input
                  type="radio"
                  name="plan"
                  checked={selectedPlan === key}
                  onChange={() => setSelectedPlan(key)}
                  style={styles.radio}
                />
              </div>
            </div>
          ))}
        </div>

        {/* What's Included */}
        <div style={styles.featuresSection}>
          <h3 style={styles.featuresTitle}>What's Included:</h3>
          <ul style={styles.featuresList}>
            <li style={styles.feature}>✓ Access to all scrolls</li>
            <li style={styles.feature}>✓ Record your own voice readings</li>
            <li style={styles.feature}>✓ Background music selections</li>
            <li style={styles.feature}>✓ Daily reminder notifications</li>
            <li style={styles.feature}>✓ Progress tracking & accountability</li>
            <li style={styles.feature}>✓ New scrolls added regularly</li>
            <li style={styles.feature}>✓ Cancel anytime</li>
          </ul>
        </div>

        {/* Payment Button */}
        <button
          style={styles.paymentButton}
          onClick={handlePayment}
          disabled={loading}
        >
          {loading ? 'Redirecting to checkout...' : `Subscribe - ${plans[selectedPlan].price}${plans[selectedPlan].period}`}
        </button>

        <p style={styles.disclaimer}>
          Secure payment processed by Stripe. Cancel anytime from your account settings.
        </p>

        <button 
          style={styles.backButton}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
  },
  card: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '600px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: '32px',
    fontSize: '1rem',
  },
  errorBox: {
    backgroundColor: '#f44336',
    color: 'white',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '20px',
    textAlign: 'center',
  },
  planContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '32px',
  },
  planCard: {
    backgroundColor: theme.colors.bgLight,
    border: `2px solid ${theme.colors.bgLight}`,
    borderRadius: '12px',
    padding: '1.5em',
    cursor: 'pointer',
    position: 'relative',
    transition: 'all 0.2s',
  },
  planCardSelected: {
    border: `2px solid ${theme.colors.teal}`,
    backgroundColor: theme.colors.bgMedium,
  },
  savingsBadge: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    backgroundColor: theme.colors.green,
    color: 'white',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
  },
  planName: {
    color: theme.colors.textPrimary,
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '12px',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: '8px',
  },
  price: {
    color: theme.colors.textPrimary,
    fontSize: '2.25rem',
    fontWeight: '700',
  },
  period: {
    color: theme.colors.textMuted,
    fontSize: '1rem',
    marginLeft: '4px',
  },
  totalPrice: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    marginBottom: '16px',
  },
  radioContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  radio: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  featuresSection: {
    backgroundColor: theme.colors.bgLight,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  featuresTitle: {
    color: theme.colors.textPrimary,
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '16px',
  },
  featuresList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  feature: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    marginBottom: '12px',
    paddingLeft: '0',
  },
  paymentButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8889em',
    color: 'white',
    fontSize: '1.125rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '16px',
  },
  disclaimer: {
    color: theme.colors.textMuted,
    fontSize: '0.75rem',
    textAlign: 'center',
    lineHeight: '1.5',
    marginBottom: '16px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.teal}`,
    color: theme.colors.teal,
    borderRadius: '8px',
    padding: '0.8571em',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },
};

export default Payment;